/**
 * SSO root tenant scope. Read after Core `/resolve-domain` returns
 * `is_root_tenant`/`root_tenant_id` and used by portal-auth + the hub
 * domain-config merge.
 *
 * Lives in a leaf module (no store imports) so authStore + brandingStore
 * can both reference it without inducing a cycle — same reason the Angular
 * version exists outside DomainConfigService and AuthService.
 */

const RESOLVED_IS_ROOT_TENANT_KEY = 'paas_resolved_is_root_tenant';
const RESOLVED_ROOT_TENANT_ID_KEY = 'paas_resolved_root_tenant_id';

export interface DomainResolutionSsoPayload {
  is_root_tenant?: boolean;
  root_tenant_id?: string;
  tenant_id: string;
}

/** Persist the flags returned by `/resolve-domain`. */
export function persistDomainResolutionSsoFlags(payload: DomainResolutionSsoPayload): void {
  if (typeof localStorage === 'undefined') return;
  const isRoot = payload.is_root_tenant === true;
  const rid = (payload.root_tenant_id ?? payload.tenant_id ?? '').trim();
  if (isRoot && rid) {
    localStorage.setItem(RESOLVED_IS_ROOT_TENANT_KEY, 'true');
    localStorage.setItem(RESOLVED_ROOT_TENANT_ID_KEY, rid);
  } else {
    localStorage.setItem(RESOLVED_IS_ROOT_TENANT_KEY, 'false');
    localStorage.removeItem(RESOLVED_ROOT_TENANT_ID_KEY);
  }
}

/**
 * Effective root tenant id for SSO scope. Falls back to the build-time
 * env value when no resolution has run yet.
 */
export function getEffectiveRootTenantId(envRootTenantId?: string | null): string | null {
  if (typeof localStorage === 'undefined') {
    return (envRootTenantId ?? '').trim() || null;
  }
  const envId = (envRootTenantId ?? '').trim() || null;
  const flag = localStorage.getItem(RESOLVED_IS_ROOT_TENANT_KEY);
  if (flag === 'true') {
    const rid = localStorage.getItem(RESOLVED_ROOT_TENANT_ID_KEY)?.trim();
    return rid || envId;
  }
  if (flag === 'false') {
    return null;
  }
  return envId;
}

export {
  RESOLVED_IS_ROOT_TENANT_KEY,
  RESOLVED_ROOT_TENANT_ID_KEY,
};
